import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import SPAButton from '../shared/SPAButton';
import { GradeSyncLink } from './gradeSyncPath';

type Refusal = { response?: { data?: { errors?: string[] } } };

interface TakeColumnButtonProps {
  /** The assignment that takes the column. */
  ownerId: number;
  lineItemId: string;
  label: string;
  stafferId: string;
  title?: string;
  /** Asked before sending, and sends `confirmed` — for a column the LMS does not vouch for. */
  confirm?: string;
}

// AIDEV-NOTE: One click, because the LMS is the authority: the column's resourceId names this
// assignment, so taking it is the same guarded repoint the picker makes, unconfirmed. Duplicate-trap
// columns never reach this button — overriding that guard broke 90 students' grades at Brandywine, and
// it is not something to offer inline in a table. A column naming a deleted assignment is the one
// exception the repoint accepts with confirmation, so that case asks first.
const TakeColumnButton = ({
  confirm, label, lineItemId, ownerId, stafferId, title,
}: TakeColumnButtonProps) => {
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);

  const repoint = useMutation({
    mutationFn: async () => axios.post(
      Routes.plato_api_sysadmin_grade_sync_repoint_path(ownerId),
      { confirmed: confirm != null, line_items_url: lineItemId },
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gradeSyncMisclaims', stafferId] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncRepairs'] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncAssignments'] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncCourseMatch', stafferId] });
    },
  });

  const refusal = (repoint.error as Refusal)?.response?.data?.errors?.[0];

  // Taking the column moves no grade by itself; the next step is sending them and watching them land.
  if (repoint.isSuccess) {
    return (
      <GradeSyncLink
        className="tw-text-sm"
        to={`/assignments/${ownerId}?verify=1`}
      >
        Linked · check it and send grades →
      </GradeSyncLink>
    );
  }

  if (asking) {
    return (
      <div className="tw-max-w-xs">
        <p className="tw-m-0 tw-text-sm tw-text-spa-primary">{confirm}</p>
        <div className="tw-mt-1 tw-flex tw-gap-1">
          <SPAButton
            disabled={repoint.isLoading}
            size="xs"
            type="button"
            variant="primary"
            onClick={() => repoint.mutate()}
          >
            Confirm
          </SPAButton>
          <SPAButton size="xs" type="button" variant="secondary" onClick={() => setAsking(false)}>
            Cancel
          </SPAButton>
        </div>
        {refusal != null && <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-spa-error">{refusal}</p>}
      </div>
    );
  }

  return (
    <div>
      <SPAButton
        disabled={repoint.isLoading}
        size="xs"
        title={title}
        type="button"
        variant="secondary"
        onClick={() => (confirm == null ? repoint.mutate() : setAsking(true))}
      >
        {label}
      </SPAButton>
      {refusal != null && <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-spa-error">{refusal}</p>}
    </div>
  );
};

export default TakeColumnButton;
